// frontend/src/utils/lookShareUtils.js
// Resolves the branded host used for Look social share URLs
// (/share/look/:id). The OG page is served by the backend, and Facebook shows
// the host of the shared URL — so we share from looks.* domains (branded)
// instead of the API host. Maps the frontend host to the matching env's looks
// subdomain. See infra: looksdemo.ogestyle.com (demo) / looks.ogestyle.com (prod)
// are custom domains bound to the demo/prod backend App Services.

export function resolveLookShareBase() {
  if (typeof window === "undefined" || !window.location) {
    return process.env.REACT_APP_API_BASE_URL || "https://looks.ogestyle.com";
  }

  const host = (window.location.hostname || "").toLowerCase();

  // Local development talks to the local backend.
  if (host === "localhost" || host === "127.0.0.1") {
    return process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
  }

  // Demo frontend hosts (demo.ogestyle.com, www.demo.ogestyle.com, *.demo.ogestyle.com)
  if (host === "demo.ogestyle.com" || host.endsWith(".demo.ogestyle.com")) {
    return "https://looksdemo.ogestyle.com";
  }

  // Production frontend hosts (www.ogestyle.com, ogestyle.com, etc.)
  return "https://looks.ogestyle.com";
}

// Version of the shared-card DESIGN. Bumping this changes every look's card URL at once (a deliberate
// redesign). A single look's photo change must never do that — each look carries its own
// `cardVersion`, which is appended after the design version: "5-3". Keep in sync with
// CARD_DESIGN_VERSION in utils/lookCardVersion.js.
export const SHARE_CARD_VERSION = "5";

/**
 * "<design>-<look>" or just "<design>" when the caller has no look version.
 *
 * The look half is what makes a re-share after new photos point at a card the social platforms have
 * never cached, without touching any other look's URL.
 */
export function resolveShareVersionToken(cardVersion) {
  const lookVersion = Number(cardVersion);
  if (!Number.isFinite(lookVersion) || lookVersion < 1) {
    return SHARE_CARD_VERSION;
  }
  return `${SHARE_CARD_VERSION}-${Math.floor(lookVersion)}`;
}

export function resolveLookShareUrl(lookId, cardVersion) {
  if (!lookId) return "";
  return `${resolveLookShareBase()}/share/look/${encodeURIComponent(String(lookId))}?v=${resolveShareVersionToken(cardVersion)}`;
}

/**
 * The square card image, for handing to the phone's share sheet.
 *
 * `inline=1` asks the endpoint for the bytes instead of its usual 302 to Azure blob.
 * The blob account sends no Access-Control-Allow-Origin at all, so a browser fetch that
 * follows that redirect is blocked - and Instagram/Threads need the actual photo. The
 * endpoint itself is CORS-enabled for our own origins, so this path works.
 *
 * See routes/lookShareRoutes.js.
 */
export function resolveLookShareImageUrl(lookId, cardVersion) {
  if (!lookId) return "";

  // Deliberately the API host, NOT the branded looks host.
  //
  // Both are bound to the same App Service and serve this route identically, but the looks
  // host is not the origin this app already talks to. On a real phone the branded host was
  // unreachable from the browser - every fetch to it failed at the network level, with no
  // status and no body - while every ordinary API call worked. The share button needs the
  // bytes, so it uses the host that is already proven to work there.
  //
  // Branding still matters for the shared LINK, which is a different URL and stays on the
  // looks host - that is the host Facebook and X show.
  const apiBase = (
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com"
  ).replace(/\/+$/, "");

  return `${apiBase}/share/look/${encodeURIComponent(String(lookId))}/image?v=${resolveShareVersionToken(cardVersion)}&inline=1`;
}

export default resolveLookShareUrl;
